import * as React from "react";
import { ExportButton } from "components/ExportButton/ExportButton";
import {
  JobRequestCollection,
  JobRequestOrigination,
  JobRequestsService,
  JobRequestStatus,
  JobRequestSummary,
  JobRequestType
} from "gen/clients";
import { formatDate } from "utils/dateUtils";
import { default as dayjs } from "dayjs";
import { JobRequestFilterSearchParams } from "../../../components/JobRequestFilter/JobRequestFilter";
import { formatPriceWithCurrency } from "../../../../../utils/currencyUtils";

interface ExportedQuoteRow {
  requestedBy: string;
  companyName: string;
  projectName: string;
  quoteAmount: string;
  created: string;
  status: string;
  rejectionReason: string;
  [key: string]: string;
}

const columnHeaders = {
  requestedBy: "Requested By",
  companyName: "Company",
  projectName: "Project Name",
  quoteAmount: "Quote Amount",
  created: "Created",
  status: "Status",
  rejectionReason: "Rejection Reason"
};

interface Props {
  filterParams?: JobRequestFilterSearchParams;
}

const ExportQuotesButton: React.FC<Props> = ({ filterParams }) => {
  const [isExporting, setIsExporting] = React.useState(false);

  const getExportData = React.useCallback(async (): Promise<ExportedQuoteRow[]> => {
    setIsExporting(true);

    try {
      let allItems: JobRequestSummary[] = [];
      let nextPageToken: string | undefined = undefined;

      // Keep fetching until no more pages
      do {
        const response: JobRequestCollection = await JobRequestsService.listJobRequests({
          pageToken: nextPageToken,
          pageSize: 100, // Fetch 100 items at a time
          requestType: filterParams?.requestType || JobRequestType.QUOTE_REQUEST,
          status: filterParams?.status,
          group: filterParams?.group,
          jobRequestId: filterParams?.requestId?.trim(),
          origination: JobRequestOrigination.WEBFORM,
          name: filterParams?.name
        });

        allItems = [...allItems, ...response.items];
        nextPageToken = response.nextPageToken;
      } while (nextPageToken);

      // Map the data to match the CSV columns
      return allItems.map(item => ({
        requestedBy: `${item.requestorFirstName || ""} ${item.requestorLastName || ""} (${item.requestorEmail})`,
        companyName: item.companyName || "",
        projectName: item.name || "",
        quoteAmount: item.autoQuote?.price
          ? formatPriceWithCurrency(item.autoQuote.price, item.autoQuote.currency)
          : "",
        created: formatDate(item.createTime),
        status: getStatusText(item.status),
        rejectionReason: item.autoQuote?.rejectionReason || ""
      }));
    } finally {
      setIsExporting(false);
    }
  }, [filterParams]);

  const getStatusText = (status: JobRequestStatus): string => {
    switch (status) {
      case JobRequestStatus.CREATED:
        return "Created";
      case JobRequestStatus.ANALYZING:
        return "Analyzing";
      case JobRequestStatus.QUOTE_READY_FOR_CLIENT_REVIEW:
        return "Ready for Client Review";
      case JobRequestStatus.QUOTE_REJECTED:
        return "Quote Rejected";
      case JobRequestStatus.QUOTE_ACCEPTED:
        return "Quote Accepted";
      case JobRequestStatus.SUBMITTING_TO_XTRF:
        return "Submitting to XTRF";
      case JobRequestStatus.SUBMITTED_TO_XTRF:
        return "Submitted to XTRF";
      case JobRequestStatus.FAILED:
        return "Failed";
      case JobRequestStatus.CANCELLED:
        return "Cancelled";
      case JobRequestStatus.COMPLETED:
        return "Completed";
      case JobRequestStatus.ARCHIVED:
        return "Archived";
      case JobRequestStatus.TRANSLATING:
        return "Translating";
      case JobRequestStatus.READY_FOR_INTERNAL_REVIEW:
        return "Ready for Internal Review";
      case JobRequestStatus.REJECTED_BY_SCREENING:
        return "Rejected by Screening";
      default:
        return status;
    }
  };

  const fileName = `quote-requests-${dayjs().format("MM-DD-YYYY")}.csv`;

  return (
    <ExportButton<ExportedQuoteRow>
      filename={fileName}
      columnHeaders={columnHeaders}
      getExportData={getExportData}
      exportingMessage="Exporting quote requests. This may take a moment..."
      variant="outlined"
      disabled={isExporting}
    >
      Export
    </ExportButton>
  );
};

export { ExportQuotesButton };
